import { useEffect } from "react";

/**
 * usePageSeo — SPA-safe <title>, <meta name="description">, <link rel="canonical">,
 * and optional JSON-LD structured data injection.
 *
 * CRA has no SSR, but Googlebot executes JS and picks up dynamically-added
 * head tags. Every field is restored to its previous value on unmount so
 * cross-route navigation doesn't leak stale metadata.
 *
 * @param {Object} opts
 * @param {string} opts.title           — full <title> string (include brand)
 * @param {string} opts.description     — 140-200 char SERP snippet
 * @param {string} [opts.path]          — canonical URL path (defaults to
 *                                        current location.pathname)
 * @param {object|object[]} [opts.jsonLd] — schema.org JSON-LD payload(s).
 * @param {string} [opts.jsonLdId]      — DOM id for the JSON-LD script tag
 *                                        (defaults to "page-jsonld"). Multiple
 *                                        pages MUST use distinct ids if any
 *                                        page mounts a second JSON-LD block.
 */
const PROD_ORIGIN = "https://dividendportfolioplanner.com";
const DEFAULT_OG_IMAGE = "https://dividendportfolioplanner.com/logo.png";

// Small helper to set-or-create a meta tag by attribute+value, remembering
// the previous value so we can restore on unmount.
function _setMeta(attr, value, content, log) {
  let tag = document.querySelector(`meta[${attr}="${value}"]`);
  const prev = tag ? tag.getAttribute("content") : null;
  const wasCreated = !tag;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  log.push({ tag, prev, wasCreated });
}

export default function usePageSeo({ title, description, path, jsonLd, jsonLdId = "page-jsonld", ogImage, ogSlug, ogType = "website", noindex = false }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    // Build the final OG image URL. Priority: explicit `ogImage` wins, else
    // an `ogSlug` maps to the backend's dynamic renderer, else fall back to
    // the site logo. Absolute URLs are required by Facebook/Twitter parsers.
    const resolvedOgImage = ogImage
      || (ogSlug ? `${PROD_ORIGIN}/api/og/${ogSlug}.png` : DEFAULT_OG_IMAGE);

    let descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag ? descTag.getAttribute("content") : null;
    if (description) {
      if (!descTag) {
        descTag = document.createElement("meta");
        descTag.setAttribute("name", "description");
        document.head.appendChild(descTag);
      }
      descTag.setAttribute("content", description);
    }

    // Per-route robots directive. Private user surfaces (auth flows, dashboard,
    // portfolio detail, payment result pages) get "noindex, nofollow" so any
    // stray link the user shares doesn't create thin indexed pages. Marketing
    // pages fall back to the site-wide index/follow directive from index.html.
    let robotsTag = document.querySelector('meta[name="robots"]');
    const prevRobots = robotsTag ? robotsTag.getAttribute("content") : null;
    if (noindex) {
      if (!robotsTag) {
        robotsTag = document.createElement("meta");
        robotsTag.setAttribute("name", "robots");
        document.head.appendChild(robotsTag);
      }
      robotsTag.setAttribute("content", "noindex, nofollow");
    }

    // Remove any pre-existing canonical tags first so we always end up with
    // exactly one on the page. Guards against a stale canonical lingering
    // when a previous route's `useEffect` cleanup fires out-of-order with
    // this mount (React concurrent-mode edge case).
    document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    let canon = null;
    const prevCanon = null;
    const canonPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonUrl = `${PROD_ORIGIN}${canonPath}`;
    canon = document.createElement("link");
    canon.setAttribute("rel", "canonical");
    document.head.appendChild(canon);
    canon.setAttribute("href", canonUrl);

    // Open Graph + Twitter Card — updated per-page so social shares show
    // the right title/description/image, not the site-wide default.
    const metaLog = [];
    if (title) {
      _setMeta("property", "og:title", title, metaLog);
      _setMeta("name", "twitter:title", title, metaLog);
    }
    if (description) {
      _setMeta("property", "og:description", description, metaLog);
      _setMeta("name", "twitter:description", description, metaLog);
    }
    _setMeta("property", "og:url", canonUrl, metaLog);
    _setMeta("property", "og:type", ogType, metaLog);
    _setMeta("property", "og:image", resolvedOgImage, metaLog);
    _setMeta("name", "twitter:image", resolvedOgImage, metaLog);

    let script = null;
    if (jsonLd) {
      document.getElementById(jsonLdId)?.remove();
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = jsonLdId;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      if (description) {
        if (prevDesc !== null) descTag.setAttribute("content", prevDesc);
        else descTag.remove();
      }
      // Restore robots meta if we overrode it for a noindex page.
      if (noindex && robotsTag) {
        if (prevRobots !== null) robotsTag.setAttribute("content", prevRobots);
        else robotsTag.remove();
      }
      if (prevCanon !== null) canon.setAttribute("href", prevCanon);
      else canon.remove();
      // Belt-and-braces cleanup: even if the router mounted a new route's
      // canonical before this cleanup fired, kill any duplicates that share
      // the exact href we just wrote.
      document.querySelectorAll(`link[rel="canonical"][href="${canonUrl}"]`).forEach((el) => el.remove());
      // Restore or remove each meta tag we touched.
      metaLog.forEach(({ tag, prev, wasCreated }) => {
        if (wasCreated) tag.remove();
        else if (prev !== null) tag.setAttribute("content", prev);
      });
      if (script) script.remove();
    };
  }, [title, description, path, JSON.stringify(jsonLd || null), jsonLdId, ogImage, ogSlug, ogType, noindex]);
}
